import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Zap, MapPin, CalendarDays, BatteryCharging, Camera, Car, Euro, Copy, MessageCircle, Facebook, Twitter, Leaf } from "lucide-react";
import { co2SavedKg, co2Trees } from "../lib/co2";
import { toast } from "sonner";
import { API_BASE, thumbUrl } from "../lib/api";
import { useGuestLoad } from "../lib/useGuestLoad";
import { GuestRetryScreen } from "../components/GuestRetryScreen";
import { TripMap } from "../components/TripMap";
import { Lightbox } from "../components/Lightbox";

const Stat = ({ icon: Icon, value, label }) => (
  <div className="text-center">
    <Icon className="w-5 h-5 text-[#74C69D] mx-auto mb-1.5" />
    <p className="font-mono font-extrabold text-xl sm:text-2xl text-white">{value}</p>
    <p className="text-[11px] tracking-wider uppercase text-white/60 mt-0.5">{label}</p>
  </div>
);

export default function RecapPage() {
  const { token } = useParams();
  const { data: trip, error, load } = useGuestLoad(`/share/${token}`);
  const [lb, setLb] = useState(null);

  if (error === "transient" && !trip) return <GuestRetryScreen onRetry={load} />;

  if (error === "invalid") {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center" data-testid="recap-not-found">
        <h1 className="font-heading font-extrabold text-2xl mb-3">Samenvatting niet gevonden</h1>
        <p className="text-muted-foreground mb-6">De link is ongeldig of de trip staat op privé.</p>
        <Link to="/" className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-6 py-3 text-sm font-semibold transition-[background-color] duration-200">Plan je eigen trip</Link>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const days = trip.days || [];
  const logs = trip.log_entries || [];
  const budget = trip.budget;
  const rc = trip.recap_costs;
  const story = trip.story;
  const chargeStops = days.reduce((a, d) => a + (d.charging_stops || []).length, 0);
  const media = logs.flatMap((e) => [
    ...(e.photo_id ? [e.photo_id] : []),
    ...(e.media || []).filter((m) => m.content_type?.startsWith("image")).map((m) => m.id),
  ]);
  const mediaUrl = (id) => `${API_BASE}/trips/${trip.id}/photos/${id}?share=${token}`;
  const pageUrl = `${window.location.origin}/recap/${token}`;
  const shareText = trip.travel_mode === "moto"
    ? `Onze motorroadtrip "${trip.name}" — ${trip.origin?.name} → ${trip.destination?.name}, ${trip.route?.distance_km} km bochtig rijplezier! 🏍️`
    : `Onze elektrische roadtrip "${trip.name}" — ${trip.origin?.name} → ${trip.destination?.name}, ${trip.route?.distance_km} km volledig elektrisch en ${co2SavedKg(trip.route?.distance_km)} kg CO₂ bespaard! ⚡🌱`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      toast.success("Link gekopieerd!");
    } catch {
      toast.info(pageUrl, { duration: 8000 });
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="recap-page">
      {lb && <Lightbox urls={lb.urls} index={lb.index} onClose={() => setLb(null)} />}
      <section className="bg-[#1B263B] relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative max-w-3xl mx-auto px-5 pt-12 pb-10 text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center"><Zap className="w-4 h-4 text-primary-foreground" /></span>
            <span className="font-heading font-extrabold text-white tracking-tight">VoltRoute</span>
          </div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#74C69D] mb-3">Elektrische roadtrip · {trip.start_date?.slice(0, 4)}</p>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight" data-testid="recap-title">{trip.name}</h1>
          <p className="text-white/70 mt-3 text-sm sm:text-base">{trip.origin?.name} → {trip.destination?.name}{trip.round_trip ? ` → ${(trip.return_end_query || "").split(",")[0] || "thuis"}` : ""} · {trip.start_date} t/m {trip.end_date}</p>
          {trip.vehicle && (
            <div data-testid="recap-vehicle" className="inline-flex items-center gap-2.5 mt-5 rounded-full bg-white/10 pl-1.5 pr-4 py-1.5">
              {trip.vehicle_photo_id ? (
                <img src={`${API_BASE}/trips/${trip.id}/photos/${trip.vehicle_photo_id}?share=${token}`} alt="Onze auto" className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <span className="w-9 h-9 rounded-full bg-primary flex items-center justify-center"><Car className="w-4 h-4 text-white" /></span>
              )}
              <span className="text-sm font-semibold text-white">{trip.vehicle?.brand} {trip.vehicle?.model}</span>
            </div>
          )}
          <div className={`grid ${rc?.kwh ? "grid-cols-5 max-w-xl" : "grid-cols-4 max-w-lg"} gap-3 mt-10 mx-auto`} data-testid="recap-stats">
            <Stat icon={MapPin} value={trip.route?.distance_km || 0} label="km" />
            <Stat icon={CalendarDays} value={days.length} label="dagen" />
            {rc?.kwh ? <Stat icon={Zap} value={`~${rc.kwh}`} label="kWh" /> : null}
            <Stat icon={BatteryCharging} value={chargeStops} label="laadstops" />
            <Stat icon={Camera} value={media.length} label="foto's" />
          </div>
          {trip.travel_mode !== "moto" && (trip.route?.distance_km || 0) > 0 && (
            <div data-testid="recap-co2" className="inline-flex items-center gap-2 mt-6 rounded-full bg-[#74C69D]/15 border border-[#74C69D]/30 px-4 py-2">
              <Leaf className="w-4 h-4 text-[#74C69D]" />
              <p className="text-xs sm:text-sm text-white/85">
                <strong className="font-mono text-[#74C69D]">{co2SavedKg(trip.route.distance_km).toLocaleString("nl-BE")} kg CO₂</strong> bespaard t.o.v. een benzineauto — evenveel als <strong className="text-[#74C69D]">{co2Trees(trip.route.distance_km)} {co2Trees(trip.route.distance_km) === 1 ? "boom" : "bomen"}</strong> die een jaar CO₂ opnemen 🌳
              </p>
            </div>
          )}
        </div>
      </section>

      <div className="h-[45vh]"><TripMap trip={trip} dark={false} shareToken={token} /></div>

      <main className="max-w-3xl mx-auto px-5 py-10 space-y-10">
        {Object.keys(trip.videos || {}).length > 0 && (
          <section data-testid="recap-video" className="bg-card rounded-2xl p-4 sm:p-6">
            <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Camera className="w-5 h-5 text-primary" /> Onze reisvideo</h2>
            <video controls playsInline preload="metadata" data-testid="recap-video-player"
              className="w-full rounded-xl bg-black max-h-[70vh]"
              src={`${API_BASE}/share/${token}/video/${trip.videos.portrait ? "portrait" : "square"}`} />
            <div className="flex flex-wrap gap-2 mt-3">
              <a data-testid="recap-video-whatsapp" target="_blank" rel="noreferrer"
                href={`https://wa.me/?text=${encodeURIComponent(`Bekijk onze elektrische roadtrip! ⚡ ${window.location.origin}/recap/${token}`)}`}
                className="rounded-full bg-[#25D366] text-white text-xs font-bold px-4 py-2 flex items-center gap-1.5 hover:opacity-90 transition-opacity duration-200">
                <MessageCircle className="w-3.5 h-3.5" /> Deel via WhatsApp
              </a>
              {Object.keys(trip.videos).map((fmt) => (
                <a key={fmt} data-testid={`recap-video-download-${fmt}`} href={`${API_BASE}/share/${token}/video/${fmt}?download=1`}
                  className="rounded-full bg-secondary text-xs font-semibold px-4 py-2 hover:bg-primary/10 transition-[background-color] duration-200">
                  Download {fmt === "portrait" ? "9:16" : "1:1"}
                </a>
              ))}
            </div>
          </section>
        )}
        {media.length > 0 && (
          <section data-testid="recap-collage">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {media.slice(0, 9).map((id, i) => (
                <img key={id} src={i === 0 ? mediaUrl(id) : thumbUrl(mediaUrl(id))} alt="Reisfoto" loading="lazy" decoding="async"
                  onClick={() => setLb({ urls: media.map(mediaUrl), index: i })}
                  className={`rounded-xl w-full object-cover cursor-zoom-in ${i === 0 ? "col-span-2 row-span-2 h-full min-h-[240px]" : "h-28 sm:h-36"}`} />
              ))}
            </div>
          </section>
        )}

        {story && (
          <article className="bg-card rounded-2xl p-6 sm:p-8" data-testid="recap-story">
            <h2 className="font-heading font-extrabold text-2xl tracking-tight mb-2">{story.title}</h2>
            <p className="text-sm text-muted-foreground italic mb-5">{story.intro}</p>
            <div className="space-y-4">
              {story.days?.map((d) => (
                <div key={d.day}>
                  <p className="text-xs tracking-[0.15em] uppercase font-semibold text-primary mb-1">Dag {d.day}</p>
                  <p className="text-sm leading-relaxed">{d.text}</p>
                </div>
              ))}
            </div>
            {story.outro && <p className="text-sm leading-relaxed mt-5 font-medium">{story.outro}</p>}
          </article>
        )}

        <section className="bg-card rounded-2xl p-6" data-testid="recap-route">
          <h2 className="font-heading font-bold text-lg mb-4">De route</h2>
          <div className="space-y-2">
            {days.map((d) => (
              <div key={d.day_number} className="flex items-center gap-3 text-sm">
                <span className="w-7 h-7 rounded-full bg-primary/10 text-primary font-mono font-bold text-xs flex items-center justify-center shrink-0">{d.day_number}</span>
                <span className="flex-1 min-w-0 truncate">{d.type === "stay" ? `Verblijf in ${d.end?.name}` : `${d.start?.name} → ${d.end?.name}`}</span>
                {d.type !== "stay" && <span className="font-mono text-xs text-muted-foreground shrink-0">{d.distance_km} km</span>}
              </div>
            ))}
          </div>
        </section>

        {(rc || budget) && (
          <section className="bg-primary rounded-2xl p-6 text-primary-foreground" data-testid="recap-budget">
            <h2 className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><Euro className="w-5 h-5" /> Wat kostte het?</h2>
            {rc ? (
              <>
                <p className="text-xs opacity-70 mb-4" data-testid="recap-costs-note">
                  {rc.actual_based ? "Op basis van de werkelijk ingevulde kosten onderweg" : "Op basis van de vooraf gemaakte raming"} · ~{rc.kwh} kWh {rc.kwh_actual ? "geladen" : "geladen (geschat)"}
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 text-center" data-testid="recap-costs-grid">
                  {[["Laden", rc.charging], ["Slapen", rc.stays], ["Eten", rc.food], ["Tol", rc.tolls], ["Extra", rc.other], ["Totaal", rc.total]]
                    .filter(([l, v]) => (v || 0) > 0 || l === "Totaal")
                    .map(([l, v]) => (
                      <div key={l} className={l === "Totaal" ? "border-l border-white/20 pl-2" : ""}>
                        <p className="font-mono font-extrabold text-lg">€ {(v || 0).toFixed(0)}</p>
                        <p className="text-[11px] tracking-wider uppercase opacity-70">{l}</p>
                      </div>
                    ))}
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center mt-3">
                {[["Laden", budget.charging_total + budget.overnight_charging_total], ["Slapen", budget.stays_total], ["Eten", budget.food_total], ["Totaal", budget.grand_total]].map(([l, v]) => (
                  <div key={l}>
                    <p className="font-mono font-extrabold text-lg">€ {v?.toFixed(0)}</p>
                    <p className="text-[11px] tracking-wider uppercase opacity-70">{l}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <section className="text-center space-y-4" data-testid="recap-share">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Deel deze reis</p>
          <div className="flex flex-wrap justify-center gap-2">
            <a data-testid="recap-share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${pageUrl}`)}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 rounded-full bg-[#25D366] text-white text-sm font-semibold px-5 py-2.5 hover:opacity-90 transition-opacity duration-200">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a data-testid="recap-share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 rounded-full bg-[#1877F2] text-white text-sm font-semibold px-5 py-2.5 hover:opacity-90 transition-opacity duration-200">
              <Facebook className="w-4 h-4" /> Facebook
            </a>
            <a data-testid="recap-share-x" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 rounded-full bg-black text-white text-sm font-semibold px-5 py-2.5 hover:opacity-90 transition-opacity duration-200">
              <Twitter className="w-4 h-4" /> X
            </a>
            <button data-testid="recap-copy-link" onClick={copyLink}
              className="flex items-center gap-2 rounded-full bg-secondary text-sm font-semibold px-5 py-2.5 hover:bg-secondary/70 transition-[background-color] duration-200">
              <Copy className="w-4 h-4" /> Kopieer link
            </button>
          </div>
        </section>

        <section className="bg-[#1B263B] rounded-3xl p-8 sm:p-10 text-center">
          <h2 className="font-heading font-extrabold text-2xl text-white tracking-tight mb-2">Zelf op elektrische roadtrip?</h2>
          <p className="text-white/70 text-sm mb-6 max-w-sm mx-auto">Plan gratis je route, laadstops, hotels en budget — binnen 2 minuten een compleet reisplan.</p>
          <Link to="/" data-testid="recap-cta" className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-[#1B4332] text-white px-7 py-3.5 font-bold active:scale-95 transition-[transform,background-color] duration-200">
            <Zap className="w-4 h-4" /> Plan je eigen trip met VoltRoute
          </Link>
        </section>
      </main>
    </div>
  );
}
